import { Injectable, inject } from '@angular/core';
import { IPolymorphicControlMeta } from '@optima-stride-ai/optima-components';
import { UtilService } from '../util.service';

/**
 * Service for applying duration constraints to form fields.
 * 
 * ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️
 * These are business rules. Should be loaded by code at runtime
 * and handled as data, not as logic.
 * ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️
 */
@Injectable({
  providedIn: 'root'
})
export class DurationConstraintsService {
  private readonly utilService = inject(UtilService);

  /**
   * Applies duration constraints to a polymorphic control field.
   * This is a pure function that returns a new IPolymorphicControlMeta with constraints applied.
   * Auto-corrects dates if they fall outside parent constraints.
   * 
   * @param durationField - The original duration field control meta
   * @param durationValues - The constraint values from parent entity (null to remove constraints)
   * @param entityType - The type of entity ('Objective' or 'Deliverable') for validation messages
   * @returns A new IPolymorphicControlMeta with constraints applied
   * 
   * @example
   * ```typescript
   * // Apply constraints from parent objective
   * const updatedField = this.durationConstraintsService.applyDurationConstraints(
   *   durationField,
   *   { from: '2024-01-01', to: '2024-12-31', value: '2024-01-01 - 2024-12-31' },
   *   'Objective'
   * );
   * ```
   */
  applyDurationConstraints(
    durationField: IPolymorphicControlMeta,
    durationValues: { from: string; to: string; value: string } | null,
    entityType: 'Objective' | 'Deliverable' = 'Objective'
  ): IPolymorphicControlMeta {
    // Early return if no constraints to apply
    if (!durationValues) {
      return { ...durationField, touched: false };
    }
    
    // Get current date values (already Date objects)
    let fromDate = durationField.dateRangeValueFrom;
    let toDate = durationField.dateRangeValueTo;
    
    // Prepare validation messages once (reused in both paths)
    const parentContext = entityType === 'Deliverable' ? 'parent objective' : '';
    const startDateLabel = `${entityType} start date`;
    const endDateLabel = `${entityType} end date`;
    const customValidationMessages = {
      dateRangeMinFrom: `${startDateLabel} must be on or after ${durationValues.from}${parentContext ? ` (${parentContext} start date)` : ''}`,
      dateRangeMinTo: `${endDateLabel} must be on or after ${durationValues.from}${parentContext ? ` (${parentContext} start date)` : ''}`,
      dateRangeMaxFrom: `${startDateLabel} must be on or before ${durationValues.to}${parentContext ? ` (${parentContext} end date)` : ''}`,
      dateRangeMaxTo: `${endDateLabel} must be on or before ${durationValues.to}${parentContext ? ` (${parentContext} end date)` : ''}`,
      dateRangeInvalid: `${entityType} start date must be before end date`,
      dateRangeSameDate: `${entityType} start date and end date cannot be the same`,
      required: `${entityType} start and end dates are required`,
    };
    
    // If dates don't exist yet (create mode), set constraints but don't validate
    // This prevents showing validation errors before user enters dates
    if (!fromDate || !toDate) {
      return {
        ...durationField,
        minDate: durationValues.from,
        maxDate: durationValues.to,
        customValidationMessages,
        touched: false,
        dirty: false,
      };
    }
    
    // Convert parent constraints to Date for comparison
    const minDate = new Date(durationValues.from);
    const maxDate = new Date(durationValues.to);
    
    // Auto-correct if outside parent constraints and track if correction occurred
    let wasAutoCorrected = false;
    
    if (fromDate < minDate || fromDate > maxDate) {
      fromDate = minDate;
      wasAutoCorrected = true;
    }
    
    if (toDate < minDate || toDate > maxDate) {
      toDate = maxDate;
      wasAutoCorrected = true;
    }
    
    // Apply constraints with corrected dates
    return {
      ...durationField,
      dateRangeValueFrom: fromDate,
      dateRangeValueTo: toDate,
      dateRangeValueFromString: this.utilService.convertDateToString(fromDate),
      dateRangeValueToString: this.utilService.convertDateToString(toDate),
      value: {
        from: this.utilService.convertDateToString(fromDate),
        to: this.utilService.convertDateToString(toDate),
      },
      minDate: durationValues.from,
      maxDate: durationValues.to,
      customValidationMessages,
      // Only set touched/dirty if auto-correction happened
      touched: wasAutoCorrected,
      dirty: wasAutoCorrected,
    };
  }
}
